import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import useSEO from "../lib/useSEO";
import { ArrowRight, ShieldCheck, MapPin, BarChart3, Layers, Check } from "lucide-react";
import { trackEvent } from "../lib/tracker";

export default function ForAttorneysPage() {
    const [pricing, setPricing] = useState([]);
    const [categories, setCategories] = useState([]);
    const [activeCat, setActiveCat] = useState("");

    useSEO({
        title: "For Attorneys — County-by-County Visibility",
        description:
            "Visibility county by county. Buy Basic, Pro, or Elite placement in the markets you actually serve — with transparent demand-band pricing across 3,142 U.S. counties.",
        path: "/for-attorneys",
    });

    useEffect(() => {
        api.get("/categories").then(({ data }) => {
            setCategories(data);
            if (data.length) setActiveCat(data[0].slug);
        });
    }, []);

    useEffect(() => {
        if (!activeCat) return;
        const loadMatrix = async () => {
            const cats = categories.length ? categories : (await api.get("/categories")).data;
            const cat = cats.find(c => c.slug === activeCat);
            if (!cat) return;
            const { data: counties } = await api.get("/counties");
            const bandSamples = {};
            for (const b of ["A", "B", "C", "D", "E"]) {
                const c = counties.find(x => x.demand_band === b);
                if (c) bandSamples[b] = c;
            }
            const rows = [];
            for (const [band, c] of Object.entries(bandSamples)) {
                try {
                    const { data: q } = await api.get(`/pricing/quote`, { params: { category_id: cat.id, county_id: c.id } });
                    rows.push({ band, ...q.pricing, top_spot_capacity: q.top_spot_capacity });
                } catch { /* ignore */ }
            }
            rows.sort((a, b) => a.band.localeCompare(b.band));
            setPricing(rows);
        };
        loadMatrix();
    }, [activeCat]); // eslint-disable-line

    return (
        <>
            {/* HERO */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12 lg:pt-20 lg:pb-16">
                <div className="grid lg:grid-cols-12 gap-10 items-start">
                    <div className="lg:col-span-7 animate-fade-up">
                        <span className="ll-pill mb-6" data-testid="for-attorneys-eyebrow">For Attorneys</span>
                        <h1 className="font-serif font-bold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-6" data-testid="for-attorneys-headline">
                            Visibility county by county.<br />
                            <span className="text-primary">Pay only for the markets you serve.</span>
                        </h1>
                        <p className="text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed mb-8">
                            Local legal answers first. Local lawyers when you need them. LocalLawFinder matches consumers to local attorneys by ZIP-mapped county and practice area.
                            Buy Basic, Featured, or Top Spot placement in the counties you actually want to take cases in — no nationwide ad spend, no wasted reach.
                        </p>
                        <div className="flex flex-wrap items-center gap-3">
                            <Link to="/attorney/auth"
                                  onClick={() => trackEvent("attorney_signup_start", "Register your practice (hero)")}
                                  className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 font-semibold hover:bg-primary/90 transition-colors"
                                  data-testid="for-attorneys-cta-register">
                                Register your practice <ArrowRight className="h-4 w-4" />
                            </Link>
                            <a href="#pricing"
                               onClick={() => trackEvent("attorney_cta_click", "See pricing")}
                               className="inline-flex items-center gap-2 rounded-full border border-foreground/20 px-6 py-3 font-semibold hover:bg-accent transition-colors"
                               data-testid="for-attorneys-cta-pricing">
                                See pricing
                            </a>
                        </div>
                    </div>

                    <div className="lg:col-span-5 animate-fade-up">
                        <div className="ll-card bg-foreground text-background border-foreground p-6 sm:p-8">
                            <div className="ll-label !text-background/60 mb-3">Why attorneys join</div>
                            <ul className="space-y-3 text-sm">
                                {[
                                    "Local-first matching: only consumers in your county see your card.",
                                    "Practice-area filtering: never pay for irrelevant leads.",
                                    "Tiered visibility: Basic, Pro, or scarce Elite.",
                                    "Transparent county-band pricing — no auctions, no surprises.",
                                    "Self-serve dashboard: turn placements on or off any time.",
                                ].map((line, i) => (
                                    <li key={i} className="flex gap-3 items-start">
                                        <Check className="h-4 w-4 mt-0.5 text-primary flex-shrink-0" />
                                        <span className="text-background/90">{line}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* VISIBILITY MODEL */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 border-t border-border">
                <div className="ll-label mb-3">County-Based Visibility Model</div>
                <h2 className="font-serif font-bold text-3xl sm:text-4xl tracking-tight mb-10">A national consumer side. A local attorney side.</h2>
                <div className="grid md:grid-cols-3 gap-6">
                    <div className="ll-card">
                        <Layers className="h-7 w-7 text-primary mb-4" />
                        <h3 className="font-serif text-xl font-semibold mb-2">Practice-area first</h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">Pick the categories you actually practice. Lawyers only appear inside the practice areas they pay for.</p>
                    </div>
                    <div className="ll-card">
                        <MapPin className="h-7 w-7 text-primary mb-4" />
                        <h3 className="font-serif text-xl font-semibold mb-2">County-by-county</h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">Buy visibility in each county separately. We map consumer ZIPs to counties and surface only the lawyers paying for that market.</p>
                    </div>
                    <div className="ll-card">
                        <BarChart3 className="h-7 w-7 text-primary mb-4" />
                        <h3 className="font-serif text-xl font-semibold mb-2">Tiered placement</h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">Basic gets you on the page. Pro ranks higher and unlocks the lead contact form. Elite leads the page in your county/category and gets leads first.</p>
                    </div>
                </div>
            </section>

            {/* TIERS */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 border-t border-border">
                <div className="ll-label mb-3">The Three Tiers</div>
                <h2 className="font-serif font-bold text-3xl sm:text-4xl tracking-tight mb-10">Match your spend to your strategy.</h2>
                <div className="grid md:grid-cols-3 gap-6">
                    <div className="ll-card">
                        <span className="ll-pill tier-basic mb-4">Basic</span>
                        <h3 className="font-serif text-2xl font-semibold mb-2">Listed and findable</h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">Appear in consumer results for the counties you choose. The entry tier — efficient for new markets.</p>
                    </div>
                    <div className="ll-card">
                        <span className="ll-pill tier-featured mb-4">Featured</span>
                        <h3 className="font-serif text-2xl font-semibold mb-2">Higher in results</h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">Featured listings rank above Basic. Best for steady-volume practices in mid-tier markets.</p>
                    </div>
                    <div className="ll-card border-primary/40">
                        <span className="ll-pill tier-top_spot mb-4">Elite</span>
                        <h3 className="font-serif text-2xl font-semibold mb-2">Premium placement</h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">Top of the consumer results page. Inventory is intentionally scarce: 3–5 slots per county per category.</p>
                    </div>
                </div>
            </section>

            {/* PRICING MATRIX */}
            <section id="pricing" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 border-t border-border">
                <div className="ll-label mb-3">Pricing</div>
                <h2 className="font-serif font-bold text-3xl sm:text-4xl tracking-tight mb-3">County-based monthly pricing by category.</h2>
                <p className="text-muted-foreground max-w-2xl mb-8">
                    Each county sits in a demand band (A is the most competitive market, E is the smallest). Each practice area has its own pricing across the three tiers.
                </p>

                <div className="flex flex-wrap gap-2 mb-8" data-testid="pricing-category-tabs">
                    {categories.map((c) => (
                        <button
                            key={c.slug}
                            onClick={() => setActiveCat(c.slug)}
                            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                                activeCat === c.slug
                                    ? "bg-primary text-primary-foreground"
                                    : "bg-card border border-border hover:bg-accent"
                            }`}
                            data-testid={`pricing-tab-${c.slug}`}>
                            {c.name}
                        </button>
                    ))}
                </div>

                <div className="ll-card overflow-x-auto">
                    <table className="w-full text-sm" data-testid="pricing-table">
                        <thead>
                            <tr className="text-left">
                                <th className="ll-label py-3 pr-6">Tier</th>
                                {pricing.map(r => (
                                    <th key={r.band} className="ll-label py-3 px-3 text-right">Band {r.band}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {[
                                { key: "basic_price", label: "Basic", desc: "Listing visibility" },
                                { key: "featured_price", label: "Pro", desc: "Higher in results + leads" },
                                { key: "top_spot_price", label: "Elite", desc: "Premium placement + leads" },
                            ].map((row) => (
                                <tr key={row.key} className="border-t border-border">
                                    <td className="py-4 pr-6">
                                        <div className="font-serif font-semibold text-base">{row.label}</div>
                                        <div className="text-xs text-muted-foreground">{row.desc}</div>
                                    </td>
                                    {pricing.map(r => (
                                        <td key={r.band} className="py-4 px-3 text-right font-semibold tabular-nums" data-testid={`price-${row.label.toLowerCase()}-${r.band}`}>
                                            ${r[row.key]}
                                            <div className="text-xs font-normal text-muted-foreground">/mo</div>
                                        </td>
                                    ))}
                                </tr>
                            ))}
                            <tr className="border-t border-border">
                                <td className="py-3 pr-6 ll-label">Elite Slots</td>
                                {pricing.map(r => (
                                    <td key={r.band} className="py-3 px-3 text-right text-xs text-muted-foreground">{r.top_spot_capacity} / county</td>
                                ))}
                            </tr>
                        </tbody>
                    </table>
                </div>

                {/* Bands explainer */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-8" data-testid="bands-explainer">
                    {[
                        { b: "A", desc: "Top-tier metros — highest demand & competition" },
                        { b: "B", desc: "Major metros — strong volume" },
                        { b: "C", desc: "Mid-tier markets — balanced" },
                        { b: "D", desc: "Smaller markets — efficient entry" },
                        { b: "E", desc: "Rural / specialty markets" },
                    ].map(({ b, desc }) => (
                        <div key={b} className="ll-card">
                            <div className="font-serif font-bold text-2xl mb-1">Band {b}</div>
                            <div className="text-xs text-muted-foreground leading-relaxed">{desc}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* DASHBOARD PREVIEW */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 border-t border-border">
                <div className="grid lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-5">
                        <div className="ll-label mb-3">Self-serve Dashboard</div>
                        <h2 className="font-serif font-bold text-3xl sm:text-4xl tracking-tight mb-4">Add or drop counties any time.</h2>
                        <p className="text-muted-foreground text-sm leading-relaxed mb-6">
                            Your dashboard shows active placements, counties covered, and practice areas at a glance.
                            Buy a placement with a real-time price quote, upgrade tiers as Elite inventory opens, or cancel — all without contacting sales.
                        </p>
                        <Link to="/attorney/auth"
                              onClick={() => trackEvent("attorney_signup_start", "Sign in / Register (dashboard preview)")}
                              className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 font-semibold hover:bg-primary/90 transition-colors"
                              data-testid="dashboard-preview-cta">
                            Sign in / Register <ArrowRight className="h-4 w-4" />
                        </Link>
                    </div>
                    <div className="lg:col-span-7">
                        <div className="ll-card">
                            <div className="grid grid-cols-3 gap-3 mb-5">
                                {[["Active placements", "12"], ["Counties covered", "5"], ["Practice areas", "2"]].map(([l, v]) => (
                                    <div key={l} className="p-3 rounded-md bg-accent/40">
                                        <div className="ll-label mb-1">{l}</div>
                                        <div className="font-serif text-2xl font-bold">{v}</div>
                                    </div>
                                ))}
                            </div>
                            <div className="space-y-2">
                                {[
                                    { area: "Criminal Defense", county: "Fulton County, GA", tier: "top_spot" },
                                    { area: "Family Law", county: "Cobb County, GA", tier: "featured" },
                                    { area: "Personal Injury", county: "DeKalb County, GA", tier: "basic" },
                                ].map((p, i) => (
                                    <div key={i} className="flex items-center justify-between p-3 rounded-md bg-card border border-border">
                                        <div>
                                            <div className="font-medium text-sm">{p.area}</div>
                                            <div className="text-xs text-muted-foreground flex items-center gap-1">
                                                <MapPin className="h-3 w-3" />{p.county}
                                            </div>
                                        </div>
                                        <span className={`ll-pill tier-${p.tier}`}>
                                            {p.tier === "top_spot" ? "Elite" : p.tier === "featured" ? "Pro" : "Basic"}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 border-t border-border">
                <div className="ll-card bg-foreground text-background border-foreground p-10 lg:p-14">
                    <div className="grid lg:grid-cols-12 gap-8 items-center">
                        <div className="lg:col-span-8">
                            <div className="ll-label !text-background/60 mb-3 flex items-center gap-2">
                                <ShieldCheck className="h-4 w-4" /> Ready when you are
                            </div>
                            <h2 className="font-serif font-bold text-3xl sm:text-4xl tracking-tight mb-3">
                                Claim your county. Start receiving local matches.
                            </h2>
                            <p className="text-background/80 text-sm sm:text-base leading-relaxed max-w-2xl">
                                Register your practice, pick the counties and practice areas you serve, and choose your tier. You can be live the same day.
                            </p>
                        </div>
                        <div className="lg:col-span-4 flex lg:justify-end">
                            <Link to="/attorney/auth"
                                  onClick={() => trackEvent("attorney_signup_start", "Register your practice (final CTA)")}
                                  className="inline-flex items-center gap-2 rounded-full bg-background text-foreground px-6 py-3 font-semibold hover:bg-background/90"
                                  data-testid="for-attorneys-final-cta">
                                Register your practice <ArrowRight className="h-4 w-4" />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}
